import { useState } from "react";
import { api } from "../data/api";
import { useTheme } from "../context/ThemeContext";

// Preference helpers — stored in localStorage, no backend needed
const PREFS_KEY = "srt-user-prefs";
function loadPrefs() {
  try { return JSON.parse(localStorage.getItem(PREFS_KEY)) || {}; } catch { return {}; }
}
function savePrefs(prefs) {
  try { localStorage.setItem(PREFS_KEY, JSON.stringify(prefs)); } catch {}
}

const LANDING_PAGES = {
  admin:      [["dashboard","Dashboard"],["affiliates","Affiliates"],["channels","Channels"],["devices","Devices"],["ota","OTA Updates"],["activity","Activity Log"]],
  superuser:  [["dashboard","Dashboard"],["affiliates","Affiliates"],["channels","Channels"],["devices","Devices"],["ota","OTA Updates"],["users","User Management"]],
  user:       [["dashboard","Dashboard"],["affiliates","Affiliates"],["devices","Devices"],["activity","Activity Log"]],
  promo:      [["dashboard","Dashboard"],["channels","Channels"]],
  custom:     [["dashboard","Dashboard"]],
};

const DATE_FORMATS = [
  ["DD/MM/YYYY", "31/12/2025"],
  ["MM/DD/YYYY", "12/31/2025"],
  ["YYYY-MM-DD", "2025-12-31"],
  ["DD MMM YYYY","31 Dec 2025"],
];

const TIMEZONES = [
  "UTC", "GMT", "America/New_York", "America/Chicago", "America/Denver",
  "America/Los_Angeles", "Europe/London", "Europe/Paris", "Asia/Kolkata",
  "Asia/Dhaka", "Asia/Singapore", "Asia/Tokyo", "Australia/Sydney",
];

function Section({ title, description, children }) {
  return (
    <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, overflow: "hidden", marginBottom: 20 }}>
      <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--border)", background: "var(--surface-alt)" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)" }}>{title}</div>
        {description && <div style={{ fontSize: 11, color: "var(--text-dim)", marginTop: 2 }}>{description}</div>}
      </div>
      {children}
    </div>
  );
}

function Row({ label, hint, children }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 20, padding: "14px 20px", borderBottom: "1px solid var(--border)" }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: "var(--ink)" }}>{label}</div>
        {hint && <div style={{ fontSize: 11, color: "var(--text-dim)", marginTop: 2 }}>{hint}</div>}
      </div>
      <div style={{ flexShrink: 0 }}>{children}</div>
    </div>
  );
}

function LastRow({ label, hint, children }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 20, padding: "14px 20px" }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: "var(--ink)" }}>{label}</div>
        {hint && <div style={{ fontSize: 11, color: "var(--text-dim)", marginTop: 2 }}>{hint}</div>}
      </div>
      <div style={{ flexShrink: 0 }}>{children}</div>
    </div>
  );
}

function Toggle({ on, onChange }) {
  return (
    <div onClick={() => onChange(!on)} style={{ width: 40, height: 22, borderRadius: 11, background: on ? "#22c55e" : "var(--border)", cursor: "pointer", position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
      <div style={{ position: "absolute", top: 3, left: on ? 20 : 3, width: 16, height: 16, borderRadius: "50%", background: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.2)", transition: "left 0.2s" }} />
    </div>
  );
}

export default function UserSettings({ user }) {
  const { mode, toggle } = useTheme();
  const prefs = loadPrefs();

  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw]         = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwSaving, setPwSaving]   = useState(false);
  const [pwMsg, setPwMsg]         = useState(null);

  const landingOptions = LANDING_PAGES[user?.role] || [["dashboard","Dashboard"]];
  const [landing, setLanding]     = useState(prefs.landing || landingOptions[0]?.[0] || "dashboard");
  const [dateFormat, setDateFormat] = useState(prefs.dateFormat || "DD/MM/YYYY");
  const [timezone, setTimezone]   = useState(prefs.timezone || "UTC");
  const [prefSaved, setPrefSaved] = useState(false);

  const [notifAccountCreated, setNotifAccountCreated] = useState(prefs.notif_accountCreated !== false);
  const [notifPasswordReset, setNotifPasswordReset]   = useState(prefs.notif_passwordReset !== false);
  const [notifSystemAlerts, setNotifSystemAlerts]     = useState(prefs.notif_systemAlerts !== false);
  const [notifSaved, setNotifSaved] = useState(false);

  const selectStyle = {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 6,
    padding: "6px 10px",
    fontSize: 12,
    color: "var(--text)",
    fontFamily: "inherit",
    outline: "none",
    cursor: "pointer",
    minWidth: 180,
  };

  const handleChangePassword = async () => {
    if (!currentPw) { setPwMsg({ type: "error", text: "Enter your current password" }); return; }
    if (newPw.length < 8) { setPwMsg({ type: "error", text: "New password must be at least 8 characters" }); return; }
    if (newPw !== confirmPw) { setPwMsg({ type: "error", text: "Passwords don't match" }); return; }
    setPwSaving(true); setPwMsg(null);
    try {
      await api.profile.changePassword({ currentPassword: currentPw, newPassword: newPw });
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
      setPwMsg({ type: "success", text: "Password changed successfully." });
    } catch (e) {
      setPwMsg({ type: "error", text: e.message || "Failed to change password" });
    } finally { setPwSaving(false); }
  };

  const handleSavePrefs = () => {
    savePrefs({ ...prefs, landing, dateFormat, timezone });
    setPrefSaved(true);
    setTimeout(() => setPrefSaved(false), 2500);
  };

  const handleSaveNotifications = () => {
    savePrefs({ ...prefs, notif_accountCreated: notifAccountCreated, notif_passwordReset: notifPasswordReset, notif_systemAlerts: notifSystemAlerts });
    setNotifSaved(true);
    setTimeout(() => setNotifSaved(false), 2500);
  };

  return (
    <div style={{ fontFamily: "'DM Sans', system-ui, sans-serif", maxWidth: 600 }}>
      <h2 style={{ fontSize: 15, fontWeight: 700, color: "var(--ink)", marginBottom: 20 }}>Settings</h2>

      <Section title="Security" description="Manage your password and account security.">
        <div style={{ padding: "16px 20px" }}>
          {pwMsg && (
            <div style={{ background: pwMsg.type === "error" ? "var(--red-bg,#fef2f2)" : "var(--green-bg,#f0fdf4)", border: `1px solid ${pwMsg.type === "error" ? "var(--red-border,#fecaca)" : "var(--green-border,#86efac)"}`, borderRadius: 6, padding: "8px 12px", fontSize: 12, color: pwMsg.type === "error" ? "var(--red,#dc2626)" : "var(--green,#16a34a)", marginBottom: 14 }}>
              {pwMsg.text}
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {[
              ["Current Password", currentPw, setCurrentPw, "••••••••"],
              ["New Password", newPw, setNewPw, "At least 8 characters"],
              ["Confirm New Password", confirmPw, setConfirmPw, "Re-enter new password"],
            ].map(([label, val, set, ph]) => (
              <div key={label}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 5 }}>{label}</div>
                <input type="password" value={val} onChange={(e) => set(e.target.value)} placeholder={ph}
                  style={{ width: "100%", boxSizing: "border-box", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 6, padding: "8px 10px", fontSize: 13, color: "var(--text)", fontFamily: "inherit", outline: "none" }} />
              </div>
            ))}
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 4 }}>
              <button onClick={handleChangePassword} disabled={pwSaving} style={{ background: "var(--ink)", border: "none", borderRadius: 6, padding: "8px 20px", fontSize: 13, fontWeight: 600, color: "#fff", cursor: pwSaving ? "not-allowed" : "pointer", opacity: pwSaving ? 0.7 : 1, fontFamily: "inherit" }}>
                {pwSaving ? "Saving…" : "Change Password"}
              </button>
            </div>
          </div>
        </div>
      </Section>

      {/* ── preferences ───────────────────────────────────────────────────── */}
      <Section title="Preferences">
        <Row label="Theme" hint="Switch between light and dark mode.">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 12, color: "var(--text-sub)" }}>{mode === "dark" ? "Dark" : "Light"}</span>
            <Toggle on={mode === "dark"} onChange={() => toggle()} />
          </div>
        </Row>

        <Row label="Default Landing Page" hint="The page you see right after logging in.">
          <select value={landing} onChange={(e) => setLanding(e.target.value)} style={selectStyle}>
            {landingOptions.map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </Row>

        <Row label="Date Format" hint="How dates are displayed throughout the app.">
          <select value={dateFormat} onChange={(e) => setDateFormat(e.target.value)} style={selectStyle}>
            {DATE_FORMATS.map(([fmt, ex]) => (
              <option key={fmt} value={fmt}>{fmt} &nbsp;({ex})</option>
            ))}
          </select>
        </Row>

        <LastRow label="Timezone" hint="Used for timestamps and activity logs.">
          <select value={timezone} onChange={(e) => setTimezone(e.target.value)} style={selectStyle}>
            {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
          </select>
        </LastRow>

        <div style={{ padding: "12px 20px", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10 }}>
          {prefSaved && <span style={{ fontSize: 12, color: "var(--green,#16a34a)" }}>✓ Preferences saved</span>}
          <button onClick={handleSavePrefs} style={{ background: "var(--ink)", border: "none", borderRadius: 6, padding: "7px 18px", fontSize: 12, fontWeight: 600, color: "#fff", cursor: "pointer", fontFamily: "inherit" }}>
            Save Preferences
          </button>
        </div>
      </Section>

      {/* ── notiifcations ─────────────────────────────────────────────────── */}
      <Section title="Notifications" description="Choose which email notifications you receive.">
        <Row label="Account created" hint="Email me when a new account is created by a Super User.">
          <Toggle on={notifAccountCreated} onChange={setNotifAccountCreated} />
        </Row>
        <Row label="Password reset" hint="Email me when my password is reset or changed.">
          <Toggle on={notifPasswordReset} onChange={setNotifPasswordReset} />
        </Row>
        {/* <LastRow>
          <Toggle on={notifSystemAlerts} onChange={setNotifSystemAlerts} />
        </LastRow> */}
        <div style={{ padding: "12px 20px", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10 }}>
          {notifSaved && <span style={{ fontSize: 12, color: "var(--green,#16a34a)" }}>✓ Preferences saved</span>}
          <button onClick={handleSaveNotifications} style={{ background: "var(--ink)", border: "none", borderRadius: 6, padding: "7px 18px", fontSize: 12, fontWeight: 600, color: "#fff", cursor: "pointer", fontFamily: "inherit" }}>
            Save
          </button>
        </div>
      </Section>
    </div>
  );
}
